import i18n from '../../../i18n';
import { DAY_MS, MINUTE_MS } from '../domain/istClock';

export { MINUTE_MS };

const HOUR_MS = 60 * MINUTE_MS;
const SECONDS_PER_MINUTE = 60;

const currentLocale = (): string => i18n.language || 'en';

const attempt = <F>(make: (locale: string) => F, locale: string): F | null => {
  try {
    return make(locale);
  } catch {
    return null;
  }
};

const cached = <F>(cache: Map<string, F | null>, key: string, make: (locale: string) => F, locale: string): F | null => {
  if (cache.has(key)) return cache.get(key) ?? null;
  const made = attempt(make, locale) ?? attempt(make, 'en');
  cache.set(key, made);
  return made;
};

const RELATIVE = new Map<string, Intl.RelativeTimeFormat | null>();
const relativeFor = (locale: string): Intl.RelativeTimeFormat | null =>
  cached(RELATIVE, locale, (l) => new Intl.RelativeTimeFormat(l, { numeric: 'always' }), locale);

export const relativeTime = (iso: string, now: number, locale: string = currentLocale()): string => {
  const relative = relativeFor(locale);
  const say = (amount: number, unit: Intl.RelativeTimeFormatUnit): string =>
    relative ? relative.format(-amount, unit) : `${amount} ${unit}${amount === 1 ? '' : 's'} ago`;
  const elapsed = Math.max(0, now - Date.parse(iso));
  if (elapsed < MINUTE_MS) return say(Math.floor(elapsed / 1000), 'second');
  if (elapsed < HOUR_MS) return say(Math.floor(elapsed / MINUTE_MS), 'minute');
  if (elapsed < DAY_MS) return say(Math.floor(elapsed / HOUR_MS), 'hour');
  return say(Math.floor(elapsed / DAY_MS), 'day');
};

type DurationUnit = 'minute' | 'second';

const DURATION = new Map<string, Intl.NumberFormat | null>();
const durationFor = (locale: string, unit: DurationUnit): Intl.NumberFormat | null =>
  cached(DURATION, `${locale}|${unit}`, (l) => new Intl.NumberFormat(l, { style: 'unit', unit, unitDisplay: 'long' }), locale);

export const formatDuration = (seconds: number, locale: string = currentLocale()): string => {
  const say = (amount: number, unit: DurationUnit): string =>
    durationFor(locale, unit)?.format(amount) ?? `${amount} ${unit}${amount === 1 ? '' : 's'}`;
  if (seconds >= SECONDS_PER_MINUTE && seconds % SECONDS_PER_MINUTE === 0) return say(seconds / SECONDS_PER_MINUTE, 'minute');
  return say(seconds, 'second');
};

const READING = new Map<string, Intl.NumberFormat | null>();
const readingFor = (locale: string): Intl.NumberFormat | null =>
  cached(READING, locale, (l) => new Intl.NumberFormat(l, { maximumFractionDigits: 2 }), locale);

export const formatReading = (value: number, locale: string = currentLocale()): string =>
  readingFor(locale)?.format(value) ?? String(Math.round(value * 100) / 100);

const ABSOLUTE_OPTIONS: Intl.DateTimeFormatOptions = {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'Asia/Kolkata',
};

const ABSOLUTE = new Map<string, Intl.DateTimeFormat | null>();
const absoluteFor = (locale: string): Intl.DateTimeFormat | null =>
  cached(ABSOLUTE, locale, (l) => new Intl.DateTimeFormat(l, ABSOLUTE_OPTIONS), locale);

export interface TimeLabel {
  readonly display: string;
  readonly absolute: string;
  readonly iso: string;
}

export const timeLabel = (iso: string, now: number, locale: string = currentLocale()): TimeLabel => {
  const absolute = absoluteFor(locale)?.format(new Date(iso)) ?? new Date(iso).toUTCString();
  const recent = now - Date.parse(iso) < DAY_MS;
  return { display: recent ? relativeTime(iso, now, locale) : absolute, absolute, iso };
};
